import React from 'react';
import { A11y, Pagination } from 'swiper/modules';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';
import { RenderNode } from '@contentful/rich-text-react-renderer';
import { Block, BLOCKS, Inline } from '@contentful/rich-text-types';
import cx from 'classnames';

import {
  IAppFeatureAnimationItem,
  IPictogramFields
} from '../../../types/contentful';
import { ScrollReveal } from '../ScrollReveal';
import { AppFeatureAnimationItem } from './AppFeatureAnimationItem';
import RichText from '../RichText';
import PictogramComponent from '../AtomicComponents/Pictogram';
import { LabelsCarousel } from './LabelsCarousel';
import { getBreakpoint } from './utils';

const disclaimerOverrideOptions: RenderNode = {
  [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children) => {
    return <p>{children}</p>;
  }
};

interface NewDesignProps {
  items: IAppFeatureAnimationItem[];
  sysId: string;
  inViewRef: (node?: Element | null) => void;
  inView: boolean;
  itemsState: Record<string, boolean>;
  selectedItemIndex: number;
  swiperReinitKey: number;
  themeClasses: {
    background: string;
    header: string;
    text: string;
    pagination: string;
    pictogramType: string;
  };
  colorTheme: string;
  invertColors: boolean;
  onMediasSwiperInit: (swiper: SwiperClass) => void;
  goToIndex: (index: number) => void;
  playItem: (item: IAppFeatureAnimationItem, play: boolean) => void;
  paginationRef: React.RefObject<HTMLDivElement>;
}

export function NewDesign({
  items,
  sysId,
  inViewRef,
  inView,
  itemsState,
  selectedItemIndex,
  swiperReinitKey,
  themeClasses,
  colorTheme,
  invertColors,
  onMediasSwiperInit,
  goToIndex,
  playItem,
  paginationRef
}: NewDesignProps) {
  const handleMediasSwiperInit = (swiper: SwiperClass) => {
    const s = swiper as unknown as {
      params: {
        pagination?: {
          el?: HTMLElement | string | null;
          clickable?: boolean;
        };
      };
      pagination?: {
        el?: HTMLElement | null;
        init?: () => void;
        update?: () => void;
        destroy?: () => void;
      };
    };

    const currentBreakpoint = getBreakpoint(window.innerWidth);
    const isDesktop = currentBreakpoint === 'desktop';

    if (!isDesktop && paginationRef?.current) {
      try {
        if (s.pagination?.destroy && s.pagination.el) {
          s.pagination.destroy();
        }
      } catch (error) {
        console.warn('Error destroying pagination:', error);
      }

      if (paginationRef.current) {
        s.params.pagination = {
          ...(s.params.pagination || {}),
          el: paginationRef.current,
          clickable: true
        };
        if (s.pagination) s.pagination.el = paginationRef.current;
        try {
          if (s.pagination?.init) s.pagination.init();
          if (s.pagination?.update) s.pagination.update();
        } catch (error) {
          console.warn('Error initializing pagination:', error);
        }
      }
    }
    onMediasSwiperInit(swiper);
  };
  return (
    <ScrollReveal className="block-container layout-padding">
      <div
        id={sysId}
        className={cx(
          'flex w-full grow flex-col items-center justify-center gap-8 rounded-card-shape px-4 py-10 tablet:px-10 tablet:py-16 desktop:flex-row desktop:gap-6 desktop:py-25',
          themeClasses.background
        )}
        ref={inViewRef}
      >
        <div
          className="order-2 flex w-full min-w-72 shrink-0 flex-col items-center justify-center gap-4 overflow-hidden tablet:items-start desktop:order-none desktop:mb-0 desktop:w-fit desktop:overflow-visible desktop:pl-0"
          data-testid="app-feature-animation-labels"
        >
          <LabelsCarousel
            key={`labels-${swiperReinitKey}`}
            items={items}
            selectedIndex={selectedItemIndex}
            onItemClick={goToIndex}
            themeClasses={themeClasses}
            colorTheme={colorTheme}
            invertColors={invertColors}
          />
        </div>
        <div
          className={cx(
            'order-1 mx-4 flex w-full shrink-0 flex-col items-center justify-center desktop:order-none desktop:mx-6 desktop:flex-row',
            items[selectedItemIndex].fields?.pictogram &&
              items[selectedItemIndex].fields?.description
              ? 'desktop:max-w-[33.3%]'
              : 'desktop:max-w-[60%]'
          )}
          data-testid="app-feature-animation-medias"
        >
          <Swiper
            key={`medias-${swiperReinitKey}`}
            className="app-feature-animation-medias h-full w-full items-start justify-center gap-2 tablet:h-[640px]"
            direction="horizontal"
            modules={[A11y, Pagination]}
            slidesPerView={1}
            speed={700}
            initialSlide={selectedItemIndex}
            pagination={{
              el: '.medias-pagination',
              clickable: true,
              enabled: true
            }}
            uniqueNavElements={false}
            onSwiper={handleMediasSwiperInit}
            breakpoints={{
              1080: {
                spaceBetween: 500,
                direction: 'vertical',
                allowTouchMove: false,
                simulateTouch: false,
                pagination: false
              }
            }}
          >
            {items.map((item) => (
              <SwiperSlide
                key={item.fields?.label}
                className={cx(
                  '!flex h-full w-full flex-col items-center justify-center',
                  themeClasses.background
                )}
                data-testid="app-feature-animation-media"
              >
                <div className="flex h-full max-h-[calc(100%-32px)] w-full flex-col items-center justify-center">
                  <AppFeatureAnimationItem
                    media={item.fields.media}
                    play={inView && itemsState[item.fields.label]}
                    onEnded={() => playItem(item, false)}
                  />
                </div>
                {item.fields.disclaimer && (
                  <div
                    className={cx(
                      'mt-4 h-full w-full text-center text-xs leading-3',
                      themeClasses.text
                    )}
                  >
                    <RichText
                      data={item.fields.disclaimer}
                      overrideOptions={disclaimerOverrideOptions}
                    />
                  </div>
                )}
              </SwiperSlide>
            ))}
          </Swiper>
          <div
            className={cx(
              'medias-pagination mt-4 flex w-fit justify-center gap-4 py-2 desktop:hidden',
              themeClasses.pagination
            )}
            ref={paginationRef}
          />
        </div>
        {items[selectedItemIndex].fields?.pictogram &&
          items[selectedItemIndex].fields?.description && (
            <div
              className={cx(
                'order-3 -mt-6 flex w-full tablet:-mt-4 desktop:order-none desktop:mt-0 desktop:w-1/4 desktop:pl-4',
                themeClasses.background
              )}
            >
              <div className="flex flex-col items-start justify-center gap-6 self-stretch">
                {items[selectedItemIndex].fields?.pictogram && (
                  <div
                    key={`pictogram-${selectedItemIndex}`}
                    className="hidden transition-all duration-500 ease-out desktop:block"
                    style={{
                      animation: 'fade-in-up 0.5s ease-out'
                    }}
                  >
                    <PictogramComponent
                      size="md"
                      type={
                        themeClasses.pictogramType as IPictogramFields['type']
                      }
                      icon={
                        items[selectedItemIndex].fields?.pictogram?.fields?.icon
                      }
                      color={undefined}
                      shape={
                        items[selectedItemIndex].fields?.pictogram?.fields
                          ?.shape
                      }
                      inverse={Boolean(invertColors)}
                    />
                  </div>
                )}
                <div
                  key={`description-${selectedItemIndex}`}
                  className={cx(
                    'typography-body-1 tablet:typography-display-5 font-normal transition-all duration-500 ease-out',
                    themeClasses.text
                  )}
                  style={{
                    animation: 'fade-in-up 0.5s ease-out'
                  }}
                >
                  {items[selectedItemIndex].fields?.description}
                </div>
              </div>
            </div>
          )}
      </div>
    </ScrollReveal>
  );
}
